/**
 * NotificationPanel — Bell icon dropdown (informational inbox).
 *
 * Shows intake.closed and intake.error notifications.
 * Click a row → marks read + opens detail sheet.
 * "Clear all" → deletes all notifications for the current user.
 */

import {
  clearAll as apiClearAll,
  dismissOne as apiDismissOne,
  markRead as apiMarkRead,
  updateArafBearerToken,
} from '@avila-roffe/api-clients-araf-factory';
import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Separator,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@avila-roffe/components-web';
import { Bell, CheckCheck, ExternalLink, X } from 'lucide-react';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from '../../stores/authStore';
import { selectUnreadCount, useNotificationStore } from '../../stores/notificationStore';
import type { AppNotification } from '../../types/notifications';
import { formatRelativeTime, routeLabel } from './notification-utils';

// ─── Event type display ───────────────────────────────────────────────────────

function eventIcon(eventType: AppNotification['eventType']): string {
  switch (eventType) {
    case 'intake.closed':
      return '✅';
    case 'intake.error':
      return '🔴';
    default:
      return '📋';
  }
}

// ─── Single notification row ──────────────────────────────────────────────────

interface NotificationRowProps {
  notification: AppNotification;
  onSelect: (n: AppNotification) => void;
  onDismiss: (id: string) => void;
}

function NotificationRow({ notification: n, onSelect, onDismiss }: NotificationRowProps) {
  return (
    <button
      type="button"
      className={`group relative flex items-start gap-3 px-4 py-3 w-full text-left cursor-pointer hover:bg-muted/50 transition-colors ${
        !n.read ? 'bg-primary/5' : ''
      }`}
      onClick={() => onSelect(n)}
    >
      {/* Unread indicator */}
      {!n.read && (
        <span className="absolute left-1.5 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-primary shrink-0" />
      )}

      <span className="text-base leading-none mt-0.5 shrink-0">{eventIcon(n.eventType)}</span>

      <div className="flex-1 min-w-0">
        <p
          className={`text-sm leading-snug truncate ${!n.read ? 'font-medium' : 'text-muted-foreground'}`}
        >
          {n.workItemTitle}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {routeLabel(n.route)} · {formatRelativeTime(n.createdAt)}
        </p>
        {n.detail && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.detail}</p>}
      </div>

      {/* Dismiss button — visible on hover */}
      <button
        type="button"
        className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(n.id);
        }}
      >
        <X className="size-3.5" />
      </button>
    </button>
  );
}

// ─── Detail sheet ─────────────────────────────────────────────────────────────

interface DetailSheetProps {
  notification: AppNotification | null;
  open: boolean;
  onClose: () => void;
}

function DetailSheet({ notification: n, open, onClose }: DetailSheetProps) {
  if (!n) return null;

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-base">
            <span>{eventIcon(n.eventType)}</span>
            <span>
              #{n.workItemId} — {n.workItemTitle}
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">{routeLabel(n.route)}</Badge>
            <Badge variant="secondary">{n.eventType.replace('intake.', '')}</Badge>
          </div>

          {n.detail && <p className="text-sm text-muted-foreground leading-relaxed">{n.detail}</p>}

          <p className="text-xs text-muted-foreground">{new Date(n.createdAt).toLocaleString()}</p>

          <div className="flex gap-2 pt-2">
            {n.cfUrl && (
              <Button
                size="sm"
                onClick={() => window.open(n.cfUrl, '_blank')}
                className="flex items-center gap-1.5"
              >
                View in Code Factory
                <ExternalLink className="size-3" />
              </Button>
            )}
            {n.adoUrl && (
              <Button
                appearance="outline"
                size="sm"
                onClick={() => window.open(n.adoUrl, '_blank')}
                className="flex items-center gap-1.5"
              >
                Open in ADO
                <ExternalLink className="size-3" />
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── NotificationPanel ────────────────────────────────────────────────────────

export function NotificationPanel() {
  const token = useAuthStore((state) => state.accessToken);

  const notifications = useNotificationStore(
    useShallow((s) => s.notifications.filter((n) => n.category === 'notification')),
  );
  const unreadCount = useNotificationStore(selectUnreadCount);
  const markRead = useNotificationStore((s) => s.markRead);
  const removeOne = useNotificationStore((s) => s.removeOne);
  const clearCategory = useNotificationStore((s) => s.clearCategory);

  const [open, setOpen] = useState(false);
  const [detailNotification, setDetailNotification] = useState<AppNotification | null>(null);

  const handleSelect = (n: AppNotification) => {
    setDetailNotification(n);
    if (!n.read) {
      markRead(n.id);
      if (token) {
        updateArafBearerToken(token);
        apiMarkRead(n.id, { recipient: '' }).catch(() => {});
      }
    }
  };

  const handleDismiss = (id: string) => {
    removeOne(id);
    if (token) {
      updateArafBearerToken(token);
      apiDismissOne(id, { recipient: '' }).catch(() => {});
    }
  };

  const handleClearAll = () => {
    clearCategory('notification');
    if (token) {
      updateArafBearerToken(token);
      apiClearAll({ recipient: '', category: 'notification' }).catch(() => {});
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            appearance="ghost"
            size="icon"
            aria-label="Notifications"
            className="relative text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
          >
            <Bell className="size-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-medium leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent align="end" className="w-80 p-0" aria-label="Notifications">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm font-semibold">Notifications</span>
            {notifications.length > 0 && (
              <Button
                appearance="ghost"
                size="sm"
                className="h-auto py-0.5 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={handleClearAll}
              >
                <CheckCheck className="size-3 mr-1" />
                Clear all
              </Button>
            )}
          </div>

          <Separator />

          {/* List */}
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
              <Bell className="size-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">All caught up</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[400px]">
              <div className="divide-y divide-border">
                {notifications.map((n) => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    onSelect={(notif) => {
                      setOpen(false);
                      handleSelect(notif);
                    }}
                    onDismiss={handleDismiss}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </PopoverContent>
      </Popover>

      <DetailSheet
        notification={detailNotification}
        open={detailNotification !== null}
        onClose={() => setDetailNotification(null)}
      />
    </>
  );
}
